import { useLiveQuery } from "dexie-react-hooks";
import { useState } from "react";
import { pickActiveContract } from "../../db/contracts";
import { getDb } from "../../db/dexie";
import { useAuthStore } from "../../store/authStore";
import {
  isSnoozed,
  saveSetup,
  setupDoneKey,
  setupNeeds,
  snooze,
  validateSetup,
  type SetupErrors,
  type SetupInput,
} from "../../setup/firstRun";
import { Button } from "../ui/Button";

/**
 * First-run card on /check: asks for MPG, non-fuel cost per mile and cut %
 * when they still sit on bootstrap defaults, so the verdict isn't guesswork.
 * Saves offline-first; hides for good once saved.
 */
export function SetupCard() {
  const me = useAuthStore((s) => s.me);
  const orgId = me?.org.id ?? "";
  const driverId = me?.driverId ?? null;

  const data = useLiveQuery(async () => {
    if (!orgId) return null;
    const db = getDb();
    const [trucks, contracts, cost, done] = await Promise.all([
      db.truck.toArray(),
      db.pay_contract.toArray(),
      driverId
        ? db.cost_profile
            .where("driverId")
            .equals(driverId)
            .filter((c) => c.deletedAt == null)
            .first()
        : Promise.resolve(undefined),
      db.meta.get(setupDoneKey(orgId)),
    ]);
    return { trucks, contracts, cost: cost ?? null, done: !!done };
  }, [orgId, driverId]);

  const [snoozed, setSnoozed] = useState(() => (orgId ? isSnoozed(orgId) : false));
  const [input, setInput] = useState<SetupInput>({ mpg: "", costPerMile: "", cutPct: "" });
  const [errors, setErrors] = useState<SetupErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Freeze the asked-for fields while the card is open so saving one field
  // doesn't make the others jump around mid-save.
  const [frozen, setFrozen] = useState<ReturnType<typeof setupNeeds>>(null);

  if (saved) {
    return (
      <p className="legal-ok card" role="status" data-testid="setup-saved" style={{ margin: 0 }}>
        Saved. Verdicts now use your numbers. Change them any time in Settings.
      </p>
    );
  }
  if (!me || !data) return null;

  const live = setupNeeds({
    me,
    trucks: data.trucks,
    cost: data.cost,
    contracts: data.contracts,
    done: data.done,
    snoozed,
  });
  const needs = frozen ?? live;
  if (!needs) return null;

  const set = (k: keyof SetupInput) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!frozen) setFrozen(live);
    setInput((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((x) => ({ ...x, [k]: undefined }));
  };

  async function onSave() {
    if (!me || !data?.cost || !needs) return;
    const res = validateSetup(input, needs);
    if (!res.ok) {
      setErrors(res.errors);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const activeContract = pickActiveContract(data.contracts);
      const truck = data.trucks.find((t) => t.deletedAt == null) ?? null;
      await saveSetup({ me, values: res.values, truck, cost: data.cost, contract: activeContract });
      setSaved(true);
    } catch (e) {
      setSaveError(
        `Couldn’t save on this device: ${e instanceof Error ? e.message : String(e)}. Try again.`,
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card stack setup-card" aria-labelledby="setup-title" data-testid="setup-card">
      <div className="stack" style={{ gap: "0.25rem" }}>
        <h2 id="setup-title" style={{ margin: 0, fontSize: "1.05rem" }}>
          Set your numbers
        </h2>
        <p className="muted" style={{ margin: 0 }}>
          Verdicts are using defaults right now. Three quick numbers make them yours.
        </p>
      </div>
      <form
        className="stack"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void onSave();
        }}
      >
        {needs.mpg ? (
          <SetupField
            id="setup-mpg"
            label="Truck MPG"
            hint="Average miles per gallon, e.g. 6.5"
            inputMode="decimal"
            value={input.mpg}
            onChange={set("mpg")}
            error={errors.mpg}
          />
        ) : null}
        {needs.costPerMile ? (
          <SetupField
            id="setup-cpm"
            label="Cost per mile, $ (not fuel)"
            hint="Tires, maintenance, insurance, truck payment spread per mile, e.g. 0.30. Fuel comes from MPG."
            inputMode="decimal"
            value={input.costPerMile}
            onChange={set("costPerMile")}
            error={errors.costPerMile}
          />
        ) : null}
        {needs.cut ? (
          <SetupField
            id="setup-cut"
            label="Cut %"
            hint="What the carrier keeps off the top. 0 if you keep the whole rate."
            inputMode="decimal"
            value={input.cutPct}
            onChange={set("cutPct")}
            error={errors.cutPct}
          />
        ) : null}
        {saveError ? (
          <p className="legal-flag" role="alert" style={{ margin: 0 }}>
            {saveError}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            variant="ghost"
            disabled={saving}
            onClick={() => {
              snooze(me.org.id);
              setSnoozed(true);
              setFrozen(null);
            }}
          >
            Not now
          </Button>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
          Works offline; it syncs when you’re back online. Change these any time in Settings.
        </p>
      </form>
    </section>
  );
}

function SetupField(props: {
  id: string;
  label: string;
  hint: string;
  value: string;
  inputMode: "decimal";
  error?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const hintId = `${props.id}-hint`;
  const errId = `${props.id}-err`;
  return (
    <div className="field">
      <label htmlFor={props.id}>{props.label}</label>
      <input
        id={props.id}
        inputMode={props.inputMode}
        autoComplete="off"
        value={props.value}
        onChange={props.onChange}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={props.error ? `${hintId} ${errId}` : hintId}
      />
      <div id={hintId} className="muted" style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}>
        {props.hint}
      </div>
      {props.error ? (
        <div id={errId} className="legal-flag" style={{ fontSize: "0.85rem", marginTop: "0.25rem" }}>
          {props.error}
        </div>
      ) : null}
    </div>
  );
}
